/**
 * The allowance — the parent's side of it, which is the only side it has.
 *
 * Two things make this screen different from every other form in the app.
 *
 * The first is that **it never asks for an age, and never shows one as a
 * field**. Age is derived from the birth month and year already on file
 * (`src/domain/age.js`), and the moment a number like that becomes editable it
 * becomes stale: the raise stops happening and nothing announces it. So the age
 * appears here only inside a sentence, as a consequence — "Ada is 9, so that's
 * $4.50 a week" — which is also the sentence that makes the formula legible
 * without explaining it.
 *
 * The second is the split. A single "how much" field would take a side in the
 * loudest argument families have about allowance, which is whether it should be
 * tied to work at all. `guaranteed_share` is that argument as a parameter: all
 * of it, half of it, or none of it. The default here is 100% — not the domain's
 * 0.5 — because the earned half has nowhere to come from yet. No gold source is
 * connected, so a family that picks 50% today would be promising a child money
 * that nothing in the product can pay. The screen says so in a plain sentence
 * rather than hiding the option.
 *
 * The preview is above the button and updates as the fields are typed, the same
 * way the savings-bonus liability is: a recurring commitment should be read
 * before it is agreed to, not discovered later.
 */

import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useFamily } from '../lib/family.jsx';
import { BackLink } from '../ui/Nav.jsx';
import { CADENCE_LABEL, CADENCE_RATE, setAllowanceRule, useBank } from '../lib/bank.js';
import {
  ALLOWANCE_DEFAULTS, allowanceAt, raiseBetween, withDefaults,
} from '../domain/allowance.js';
import { MONTH_NAMES, nextRaiseDate } from '../domain/age.js';
import { CADENCES } from '../domain/periods.js';
import { centsFromDollars, formatCents } from '../domain/money.js';

const dollars = (cents) => (cents / 100).toFixed(2);

/**
 * The three answers, in the words families actually use.
 *
 * Presets rather than a free percentage: the interesting values are 100, 50 and
 * 0, and a slider that can land on 37% invites a parent to tune a number whose
 * only real meaning is which of three positions they hold.
 */
const SHARE_CHOICES = [
  { share: 1, label: 'All of it', hint: 'no strings' },
  { share: 0.5, label: 'Half', hint: 'the rest is earned' },
  { share: 0, label: 'None of it', hint: 'all earned' },
];

/** The raise, as a date a parent can put on the calendar. */
function raiseMonth(dateOnly) {
  const [year, month] = String(dateOnly).split('-').map(Number);
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

export default function AllowanceRule() {
  const { childId } = useParams();
  const { family, kids, profile } = useFamily();
  const bank = useBank(family?.id);
  const navigate = useNavigate();
  // `null` means "whatever the saved rule says", so the fields fill themselves
  // in when the rule finishes loading without an effect racing the typing.
  const [rate, setRate] = useState(null);
  const [cadence, setCadence] = useState(null);
  const [share, setShare] = useState(null);
  const [rounding, setRounding] = useState(null);
  const [floor, setFloor] = useState(null);
  const [ceiling, setCeiling] = useState(null);
  const [from, setFrom] = useState(null);
  const [advanced, setAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // What the household pays is a grown-up's decision. A child who could set
  // their own allowance has a money printer, the same as with the bonus rate.
  if (profile) return <Navigate to="/" replace />;

  const child = kids.find((k) => k.id === childId);
  const live = bank.allowanceRules[childId] ?? null;

  const rateValue = rate ?? String(live?.dollarsPerYearOfAge ?? ALLOWANCE_DEFAULTS.dollarsPerYearOfAge);
  const cadenceValue = cadence ?? live?.cadence ?? ALLOWANCE_DEFAULTS.cadence;
  // 100% by default, not the domain's 0.5 — see the note at the top of the file.
  const shareValue = share ?? live?.guaranteedShare ?? 1;
  const roundingValue = rounding ?? dollars(live?.roundingCents ?? ALLOWANCE_DEFAULTS.roundingCents);
  const floorValue = floor ?? dollars(live?.floorCents ?? ALLOWANCE_DEFAULTS.floorCents);
  const ceilingValue = ceiling ?? dollars(live?.ceilingCents ?? ALLOWANCE_DEFAULTS.ceilingCents);
  const fromValue = from ?? bank.today;

  // The rule as the domain understands it, rebuilt on every keystroke. The
  // preview below and the row that gets inserted are the same object, so what a
  // parent reads is what the job will pay.
  const rule = withDefaults({
    dollarsPerYearOfAge: Number(rateValue) || 0,
    guaranteedShare: Number(shareValue),
    cadence: cadenceValue,
    roundingCents: centsFromDollars(roundingValue),
    floorCents: centsFromDollars(floorValue),
    ceilingCents: centsFromDollars(ceilingValue),
  });

  // Birth month and year, never an age. The domain derives the rest.
  const birth = child ? { birthMonth: child.birth_month, birthYear: child.birth_year } : null;
  const preview = birth ? allowanceAt(bank.today, birth, rule) : null;
  const raise = birth
    ? raiseBetween(bank.today, nextRaiseDate(bank.today, birth), birth, rule)
    : null;

  const name = child?.display_name ?? 'They';
  const perPeriod = CADENCE_RATE[cadenceValue] ?? CADENCE_RATE.weekly;
  // The database says the same thing (`ceiling_cents >= floor_cents`), but a
  // check constraint surfaces as "new row violates…" after the tap. Caught here
  // so the sentence a parent reads is about their two numbers.
  const limitsInverted = rule.floorCents > rule.ceilingCents;

  async function save(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await setAllowanceRule({
        familyId: family.id,
        childId,
        dollarsPerYearOfAge: rule.dollarsPerYearOfAge,
        guaranteedShare: rule.guaranteedShare,
        cadence: rule.cadence,
        roundingCents: rule.roundingCents,
        floorCents: rule.floorCents,
        ceilingCents: rule.ceilingCents,
        effectiveFrom: fromValue,
      });
      navigate(`/bank/${childId}`, { replace: true });
    } catch (err) {
      setError(`That allowance didn’t save — ${err.message}.`);
      setBusy(false);
    }
  }

  return (
    <main className="app-shell stack">
      <div className="stack tight">
        <BackLink to={`/bank/${childId}`}>{child?.display_name ?? 'Bank'}</BackLink>
        <h1>Allowance</h1>
      </div>

      <p className="muted">
        {name} gets paid on a schedule, and the amount follows their age on its own —
        you set the rate once and it keeps up.
      </p>

      {bank.error && <p className="notice error">{bank.error}</p>}
      {error && <p className="notice error" role="alert">{error}</p>}
      {bank.loading && <p className="muted" role="status">Loading…</p>}

      <form className="stack" onSubmit={save}>
        <div className="card stack">
          <div className="row">
            <label style={{ flex: '1 1 9rem' }}>
              Dollars per year of age
              <input
                type="number" inputMode="decimal" min="0" step="0.05" required
                value={rateValue} onChange={(e) => setRate(e.target.value)}
              />
            </label>
            <label style={{ flex: '1 1 9rem' }}>
              How often
              <select value={cadenceValue} onChange={(e) => setCadence(e.target.value)}>
                {Object.keys(CADENCES).map((c) => (
                  <option key={c} value={c}>{CADENCE_LABEL[c]}</option>
                ))}
              </select>
            </label>
          </div>
          <p className="small muted">
            The old rule of thumb is half a dollar for each year of age, a week. It is a
            starting point, not a law — this is the number that changes it.
          </p>
        </div>

        <div className="card stack tight" role="group" aria-labelledby="share-label">
          <p className="eyebrow" id="share-label">How much of it is guaranteed</p>
          <div className="row">
            {SHARE_CHOICES.map((c) => (
              <button
                key={c.share}
                type="button"
                aria-pressed={Number(shareValue) === c.share}
                className={Number(shareValue) === c.share ? 'primary' : ''}
                style={{ flex: '1 1 7rem' }}
                onClick={() => setShare(c.share)}
              >
                {c.label}
                <span className="small" style={{ display: 'block', fontWeight: 'normal' }}>
                  {c.hint}
                </span>
              </button>
            ))}
          </div>
          {/*
            The honest sentence, and the reason the default is 100%. Phase 5 is
            where a gold source gets connected; until then the earned share is a
            promise with no payer, and a parent choosing it should know that
            before their child does.
          */}
          <p className="small muted">
            The earned part only pays once a gold source is connected, and nothing pays it
            today — so anything you leave un-guaranteed simply isn’t paid yet.
          </p>
        </div>

        <div className="card stack tight">
          <label>
            Starting from
            <input type="date" value={fromValue} onChange={(e) => setFrom(e.target.value)} />
          </label>
          {/*
            Not the same rule the savings bonus follows, and deliberately: a
            bonus is earned across a period, so the one it starts inside is not
            owed, while an allowance is a payment for a period and “starting
            today” has to mean this one.
          */}
          <p className="small muted">
            Whichever period this date lands in gets paid, so picking today pays for now
            rather than from next time.
          </p>
        </div>

        <div className="card stack tight">
          <div className="spread">
            <p className="eyebrow">Rounding and limits</p>
            <button type="button" className="quiet" onClick={() => setAdvanced((a) => !a)}>
              {advanced ? 'Hide' : 'Change'}
            </button>
          </div>
          {!advanced && (
            <p className="small muted">
              Rounded to the nearest {formatCents(rule.roundingCents)}, never above{' '}
              {formatCents(rule.ceilingCents)}
              {rule.floorCents > 0 && ` or below ${formatCents(rule.floorCents)}`}.
            </p>
          )}
          {advanced && (
            <>
              <div className="row">
                <label style={{ flex: '1 1 7rem' }}>
                  Round to nearest
                  <input
                    type="number" inputMode="decimal" min="0.01" step="0.05"
                    value={roundingValue} onChange={(e) => setRounding(e.target.value)}
                  />
                </label>
                <label style={{ flex: '1 1 7rem' }}>
                  Never less than
                  <input
                    type="number" inputMode="decimal" min="0" step="0.5"
                    value={floorValue} onChange={(e) => setFloor(e.target.value)}
                  />
                </label>
                <label style={{ flex: '1 1 7rem' }}>
                  Never more than
                  <input
                    type="number" inputMode="decimal" min="0" step="0.5"
                    value={ceilingValue} onChange={(e) => setCeiling(e.target.value)}
                  />
                </label>
              </div>
              {/*
                Rounding is not cosmetic here: the parent settles this in coins,
                and $4.50 is a coin while $4.37 is an argument.
              */}
              <p className="small muted">
                Rounding is in real money — you hand this over in coins. The ceiling is the
                one that matters as they get older, since the formula climbs forever.
              </p>
            </>
          )}
        </div>

        {/*
          The whole point of the screen: the number, in words, before the button.
          Nothing here is editorial — it states what the formula produces and
          what part of it is actually paid.
        */}
        {preview && (
          <div className="card stack tight">
            <p className="eyebrow">What this comes to</p>
            <p>
              {name} is {preview.age}, so that’s{' '}
              <strong className="money">{formatCents(preview.targetCents)}</strong> {perPeriod},
              and <strong className="money">{formatCents(preview.baseCents)}</strong> of it is
              paid automatically.
            </p>
            {preview.expectedEarnedCents > 0 && (
              <p className="small muted">
                The other {formatCents(preview.expectedEarnedCents)} is the part they earn.
              </p>
            )}
            {raise && (
              <p className="small muted">
                Goes up to {formatCents(raise.toTargetCents)} on{' '}
                {raiseMonth(nextRaiseDate(bank.today, birth))}.
              </p>
            )}
          </div>
        )}

        {limitsInverted && (
          <p className="notice warn" role="alert">
            The lowest is above the highest. One of those two has to move before this
            can be saved.
          </p>
        )}

        <button className="primary" type="submit" disabled={busy || !child || limitsInverted}>
          {busy ? 'Saving…' : 'Save this allowance'}
        </button>
      </form>

      {live && birth && (
        <p className="small muted">
          Now: {formatCents(allowanceAt(bank.today, birth, live).baseCents)}{' '}
          {CADENCE_RATE[live.cadence] ?? CADENCE_RATE.weekly}, since {live.effectiveFrom}. Saving a
          new one leaves the old rule in place for the history it already explains.
        </p>
      )}
    </main>
  );
}
